import { Component, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, catchError, of, takeUntil } from 'rxjs';
import { SettingsService } from 'app/modules/admin/settings/settings.service';

import { ChatWidgetComponent } from 'app/shared/components/chat-widget/chat-widget.component';

@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    styles: [`
        .mobile-nav-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            padding: 6px 8px;
            color: rgba(255, 255, 255, 0.7);
            text-decoration: none;
            border: none;
            background: none;
            cursor: pointer;
            min-width: 56px;
        }
        .mobile-nav-item mat-icon { color: rgba(255, 255, 255, 0.7); }
        .mobile-nav-item.active-mobile-link { color: #818cf8; }
        .mobile-nav-item.active-mobile-link mat-icon { color: #818cf8; }
        .mobile-nav-item.text-white { color: white; }
        .mobile-nav-item.text-white mat-icon { color: white; }
        .mobile-nav-icon { font-size: 22px; width: 22px; height: 22px; }
        .mobile-nav-label { font-size: 10px; font-weight: 500; white-space: nowrap; }
    `],
    encapsulation: ViewEncapsulation.None,
    imports: [
    FuseLoadingBarComponent,
    MatButtonModule,
    MatIconModule,
    UserComponent,
    LanguagesComponent,
    FuseFullscreenComponent,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    FuseVerticalNavigationComponent,

    ChatWidgetComponent,
],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
    config: FuseConfig;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    webchatEnabled = signal(false);
    mobileMenuOpen = signal(false);

    // All nav items for the "More" menu
    get allMobileNavItems(): { id: string; title: string; icon: string; link: string; locked: boolean }[] {
        return (this.navigation?.compact ?? [])
            .filter((item) => item.type === 'basic' && !!item.link)
            .map((item) => ({
                id: item.id,
                title: item.title,
                icon: item.icon ?? '',
                link: item.link,
                locked: (item.classes?.wrapper ?? '').includes('altura-nav-locked'),
            }));
    }

    toggleMobileMenu(): void {
        this.mobileMenuOpen.update(v => !v);
    }

    navigateMobile(link: string): void {
        this.mobileMenuOpen.set(false);
        this._router.navigateByUrl(link);
    }
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfigService: FuseConfigService,
        private _userService: UserService,
        private _settingsService: SettingsService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;
            });
        
        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Load webchat setting and subscribe to changes
        this._settingsService.getWebchatEnabled()
            .pipe(takeUntil(this._unsubscribeAll), catchError(() => of(null)))
            .subscribe();

        this._settingsService.webchatEnabled$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((enabled) => this.webchatEnabled.set(enabled));
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this._fuseConfigService.config = { scheme };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
